/* eslint-disable react/no-danger */
/* eslint-disable max-len */
import {
  SaveOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutoComplete,
  Button, Card, Col,
  message,
  Modal,
  Row, Space, Spin, Typography
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import {
  Controller, FormProvider, useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Picker } from '../Picker';

import { useAppSelector } from 'app/store';
import { DropdownElement } from 'common/components/DropdownType';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import UploadPhoto, { UploadFileType } from 'common/components/UploadPhoto';
import { ocrStaffInfoService } from 'common/services/extends/staff';
import { StaffContractEnum, StaffResponseType } from 'common/services/extends/staff/types';
import { createStaffAssignService, getInfoStaffAssignByExistedIdCard, updateStaffAssignService } from 'common/services/extends/staffAssign';
import { StaffAssignDetailResponseType, StaffAssignRequestType } from 'common/services/extends/staffAssign/types';
import { contractTypeOptions, NoTaxCode, ROUTE_PATHS } from 'common/utils/constant';
import { detectErrorByCode, formatDateYYYYMMDD } from 'common/utils/functions';
import { createDetailStaffAssignSchema, updateDetailStaffAssignSchema } from 'common/utils/schemas';
import roles, { getPermission } from 'configs/roles';
import { TableDataTypes } from 'extends/EmployeeAssign';

export type StaffFormReturnTypes = Omit<TableDataTypes, 'id'>;
export type CreateEmployeeAssignProps = {
  data?: StaffAssignDetailResponseType;
  loading?: boolean;
  program?: {
    handleClosePopup?: () => void;
    handleSubmit?: () => void;
  }
} & ActiveRolesExpend;

type FormReturnTypes = Omit<TableDataTypes, 'id'> & {
  idCardFrontImage?: UploadFileType;
  idCardFrontPath?: string;
  idCardBackImage?: UploadFileType;
  idCardBackPath?: string;
};

const defaultValues: FormReturnTypes = {
  fullname: '',
  taxCode: '',
  phoneNumber: '',
  position: '',
  idCardNumber: '',
  idCardIssuedAt: undefined,
  idCardIssuedBy: '',
  permanentAddress: '',
  dob: undefined,
  idCardFrontImage: undefined,
  idCardFrontPath: '',
  idCardBackImage: undefined,
  idCardBackPath: '',
  contractType: {
    label: StaffContractEnum[2],
    value: StaffContractEnum.HDGV
  }
};

const CreateEmployeeAssign: React.FC<CreateEmployeeAssignProps> = ({
  roleIndex, roleUpdate, roleOther, loading = false, data, program
}) => {
  /* Hooks */
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const idParams = Number(searchParams.get('id'));
  const { roles: totalRoles } = useAppSelector((state) => state.auth);

  /* States */
  const [viewOnly, setViewOnly] = React.useState<boolean>(false);
  const [existedInfo, setExistedInfo] = React.useState<{ idNumber: string, info: StaffResponseType | StaffAssignDetailResponseType | null } | null>(null);
  const [contractTypeState, setContractTypeState] = React.useState<StaffContractEnum>(StaffContractEnum.HDGV);
  const [hasCheckedIdCard, setHasCheckedIdCard] = React.useState<boolean>(false);
  const [isNotMatchIdCardNumber, setIsNotMatchIdCardNumber] = React.useState<boolean>(false);

  /* Check permission */
  const canAccessOCR = getPermission(roleOther || [], roles.EMPLOYEE_ASSIGN_OCR) || totalRoles.includes('*');

  /* React-hook-form */
  const method = useForm<FormReturnTypes>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(idParams ? updateDetailStaffAssignSchema : createDetailStaffAssignSchema)
  });

  /* Queries */

  // Watch front/back id card path
  const {
    idCardFrontImage: isDirtyFrontIdCard,
    idCardBackImage: isDirtyBackIdCard
  } = method.formState.dirtyFields;

  const frontIdentityPath = isDirtyFrontIdCard ? method.watch('idCardFrontImage')?.pathSubmit || '' : (data?.staffAssignData?.frontIdCardPath || '');
  const backIdentityPath = isDirtyBackIdCard ? method.watch('idCardBackImage')?.pathSubmit || '' : (data?.staffAssignData?.backIdCardPath || '');
  const { isDirty: isDirtyIdCardFrontImage } = method.getFieldState('idCardFrontImage');
  const { isDirty: isDirtyIdCardBackImage } = method.getFieldState('idCardBackImage');

  const { isLoading: isLoadingInfoFromOCR, mutate: mutateInfoFromOCR } = useMutation(
    ['getInfoFromOCR-staff-assign', frontIdentityPath, backIdentityPath],
    () => ocrStaffInfoService({
      backIdCardPath: backIdentityPath,
      frontIdCardPath: frontIdentityPath,
    }),
    {
      onSuccess: (res) => {
        if (res) {
          const valueForm = method.getValues();
          const {
            idCardAddress, idCardDob, idCardIssuedAt, idCardIssuedBy, idCardName, idCardNumber
          } = res.data;
          if (!!idParams && res.data.idCardNumber !== valueForm.idCardNumber) {
            setIsNotMatchIdCardNumber(true);
          } else {
            method.reset({
              ...valueForm,
              permanentAddress: idCardAddress || valueForm.permanentAddress,
              dob: idCardDob || valueForm.dob,
              fullname: idCardName || valueForm.fullname,
              idCardIssuedAt: idCardIssuedAt || valueForm.idCardIssuedAt,
              idCardIssuedBy: idCardIssuedBy || valueForm.idCardIssuedBy,
              idCardNumber,
            });
          }
        }
      },
      onError: (errors: any) => {
        if (errors && Array.isArray(errors) && errors.length > 0) {
          errors.forEach((item) => {
            method.setError('idCardFrontImage', {
              message: t(`${detectErrorByCode(item.code)}`),
              type: 'validate'
            });
          });
        }
      },
    }
  );

  const { mutate: createMutate, isLoading: createLoading } = useMutation(
    ['create-staff-assign'],
    createStaffAssignService,
    {
      onSuccess: () => {
        if (program && program.handleClosePopup) {
          program.handleClosePopup();
        } else {
          navigate(ROUTE_PATHS.EMPLOYEE_ASSIGN_MANAGEMENT);
        }
        message.success(t('message.createSuccess'));
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            method.setError(
              e.field as keyof FormReturnTypes,
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.updateError'));
        }
      }
    }
  );

  const { mutate: updateMutate, isLoading: updateLoading } = useMutation(
    ['update-staff-assign'],
    updateStaffAssignService,
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            method.setError(
              e.field as keyof FormReturnTypes,
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.updateError'));
        }
      }
    }
  );
  /* Functions */
  const onSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) {
      return;
    }

    const formData = method.getValues();
    const {
      fullname, dob, taxCode, idCardNumber,
      idCardIssuedAt, idCardIssuedBy, permanentAddress,
      idCardFrontImage, idCardFrontPath,
      idCardBackImage, idCardBackPath,
      phoneNumber, position
    } = formData;
    const reqObj: StaffAssignRequestType = {
      fullname,
      dob: formatDateYYYYMMDD(dob),
      taxCode: taxCode === NoTaxCode.value ? '' : taxCode,
      phoneNumber,
      idCardNumber,
      position,
      idCardIssuedAt: formatDateYYYYMMDD(idCardIssuedAt),
      idCardIssuedBy: idCardIssuedBy || '',
      permanentAddress: permanentAddress || '',
      frontIdCard: idCardFrontImage?.file ? idCardFrontImage?.pathSubmit : idCardFrontPath || '',
      backIdCard: idCardBackImage?.file ? idCardBackImage?.pathSubmit : idCardBackPath || '',
    };
    if (idParams) {
      updateMutate({
        id: idParams,
        data: reqObj
      });
    } else {
      createMutate(reqObj);
    }
  };

  const { mutate: getInfoMutate, isLoading: getInfoByIdCardLoading } = useMutation(
    ['get-info-staff-assign-by-idCard'],
    getInfoStaffAssignByExistedIdCard,
    {
      onSuccess(res, param) {
        setExistedInfo({ idNumber: param, info: res });
        if (res) setContractTypeState((res as StaffAssignDetailResponseType).staffAssignData ? StaffContractEnum.HDGV : StaffContractEnum['Part-time']);
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            method.setError(
              e.field as keyof FormReturnTypes || 'idCardNumber',
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.anErrorHasOccurred'));
        }
      }
    }
  );

  const onReuseInfo = () => {
    if (existedInfo?.info) {
      const {
        fullname,
        dob,
        idCardIssuedAt,
        idCardIssuedBy,
        idCardNumber,
        permanentAddress,
        taxCode,
        backIdCardPath,
        backIdCard,
        frontIdCard,
        frontIdCardPath,
        phoneNumber,
        contractType
      } = (existedInfo.info as StaffAssignDetailResponseType).staffAssignData || (existedInfo.info as StaffResponseType).staffData;
      method.reset({
        fullname,
        taxCode: taxCode === '' ? NoTaxCode.value : taxCode,
        idCardNumber,
        idCardIssuedBy,
        permanentAddress,
        dob,
        idCardIssuedAt,
        idCardFrontPath: frontIdCardPath || '',
        idCardFrontImage: {
          file: undefined,
          pathSubmit: frontIdCard || '',
        },
        idCardBackPath: backIdCardPath || '',
        idCardBackImage: {
          file: undefined,
          pathSubmit: backIdCard || '',
        },
        phoneNumber,
        contractType: {
          label: StaffContractEnum[contractType],
          value: contractType,
        },
      });
      setExistedInfo(null);
      setViewOnly(true);
    } else {
      setExistedInfo(null);
      method.reset({
        ...defaultValues,
        idCardNumber: method.getValues('idCardNumber')
      });
    }
    setHasCheckedIdCard(true);
  };

  const renderReuseMessage = useMemo(() => {
    let reuseMessage = t('employee.idCardNotExist');
    if (existedInfo?.info) {
      if (contractTypeState === StaffContractEnum['Part-time']) reuseMessage = t('employee.idCardIsExistedStaff');
      if (contractTypeState === StaffContractEnum.HDGV) reuseMessage = t('employee.idCardIsExistedStaffAssign');
    }
    return reuseMessage;
  }, [existedInfo?.info, contractTypeState, t]);

  useEffect(() => {
    if (data) {
      const {
        fullname,
        dob,
        idCardIssuedAt,
        idCardIssuedBy,
        idCardNumber,
        permanentAddress,
        taxCode,
        backIdCardPath,
        backIdCard,
        frontIdCard,
        frontIdCardPath,
        phoneNumber,
        position
      } = data.staffAssignData;

      method.reset({
        fullname,
        taxCode: taxCode === '' ? NoTaxCode.value : taxCode,
        idCardNumber,
        idCardIssuedBy,
        permanentAddress,
        dob,
        position,
        idCardIssuedAt,
        idCardFrontPath: frontIdCardPath || '',
        idCardFrontImage: {
          file: undefined,
          pathSubmit: frontIdCard || '',
        },
        idCardBackPath: backIdCardPath || '',
        idCardBackImage: {
          file: undefined,
          pathSubmit: backIdCard || '',
        },
        phoneNumber
      });
    }
  }, [data]);

  /* Render */
  return (
    <>
      {
        !program && (
          <HeaderPage
            fixed
            title={t('sidebar.employeeAssign')}
            rightHeader={(
              <Space size={16}>
                <Button
                  type="primary"
                  disabled={(Boolean(idParams) && !roleUpdate) || viewOnly || (!hasCheckedIdCard && !idParams)}
                  onClick={method.handleSubmit(onSubmit)}
                >
                  <SaveOutlined />
                  {t('system.save')}
                </Button>
              </Space>
            )}
          />
        )
      }
      <Spin size="large" spinning={loading || updateLoading || createLoading || isLoadingInfoFromOCR}>
        <div className="t-mainlayout_wrapper">
          <FormProvider {...method}>
            <Row gutter={16}>
              <Col xxl={24}>
                <Card type="inner">
                  <div className="site-card-border-less-wrapper">
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <Card
                        title={<Typography.Title level={4}>{t('employee.information')}</Typography.Title>}
                        className="u-mt-16"
                      // extra={(
                      //   <>
                      //     <Button
                      //       type="primary"
                      //       onClick={() => { }}
                      //       icon={<CameraOutlined />}
                      //       style={{ marginRight: 8 }}
                      //     >
                      //       {t('employee.takePhoto')}
                      //     </Button>
                      //     <Button
                      //       type="primary"
                      //       onClick={() => { }}
                      //       icon={<CloudUploadOutlined />}
                      //     >
                      //       {t('employee.uploadImg')}
                      //     </Button>
                      //   </>
                      // )}
                      >
                        <Row gutter={16}>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('employee.identityNumber')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="idCardNumber"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Space className="idCardNumberInput" style={{ width: '100%', alignItems: 'flex-start' }}>
                                  <Input
                                    className="u-mt-8"
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e.target.value);
                                      if (hasCheckedIdCard) setHasCheckedIdCard(false);
                                    }}
                                    error={error?.message}
                                    size="large"
                                    disabled={!!idParams || viewOnly}
                                  />
                                  <Button
                                    loading={getInfoByIdCardLoading}
                                    size="large"
                                    type="primary"
                                    className="u-mt-8"
                                    onClick={() => {
                                      if (viewOnly) {
                                        setViewOnly(false);
                                        setHasCheckedIdCard(false);
                                        method.reset({
                                          ...defaultValues,
                                        });
                                      } else {
                                        getInfoMutate(value);
                                      }
                                    }}
                                    disabled={!roleIndex || !!idParams || !method.getValues('idCardNumber')}
                                  >
                                    {viewOnly ? (
                                      t('employee.editIdCard')
                                    ) : (
                                      t('employee.checkIdCard')
                                    )}
                                  </Button>
                                </Space>
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('employee.grantDate')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="idCardIssuedAt"
                              control={method.control}
                              render={({ field, fieldState: { error } }) => (
                                <Picker
                                  className="u-mt-8"
                                  size="large"
                                  value={field.value ? dayjs(field.value) : undefined}
                                  onChange={field.onChange}
                                  format="DD/MM/YYYY"
                                  style={{ width: '100%' }}
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                  error={error?.message}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('employee.grantPlace')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="idCardIssuedBy"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('assignmentStaff.fullname')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="fullname"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('employee.birthDate')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="dob"
                              control={method.control}
                              render={({ field, fieldState: { error } }) => (
                                <Picker
                                  className="u-mt-8"
                                  size="large"
                                  value={field.value ? dayjs(field.value) : undefined}
                                  onChange={field.onChange}
                                  format="DD/MM/YYYY"
                                  style={{ width: '100%' }}
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                  error={error?.message}
                                  disableDateUnder18age
                                />
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('employee.taxNumber')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="taxCode"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <>
                                  <AutoComplete
                                    className="u-mt-8"
                                    style={{ width: '100%' }}
                                    value={value}
                                    onChange={onChange}
                                    options={[NoTaxCode]}
                                    size="large"
                                    disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                  >
                                    <Input
                                      value={value ?? undefined}
                                      onChange={onChange}
                                      size="large"
                                      disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                      allowClear
                                    />
                                  </AutoComplete>

                                  {error && (
                                    <span
                                      className="a-input_errorMessage"
                                    >
                                      {error.message}
                                    </span>
                                  )}
                                </>
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('system.phone')}
                            </Typography.Text>
                            {' '}
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="phoneNumber"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('employee.address')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="permanentAddress"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('system.position')}
                            </Typography.Text>
                            {' '}
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="position"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Card
                            extra={canAccessOCR ? (
                              <Button
                                type="primary"
                                disabled={isLoadingInfoFromOCR || !frontIdentityPath || !backIdentityPath || !(isDirtyIdCardFrontImage || isDirtyIdCardBackImage)}
                                onClick={() => mutateInfoFromOCR()}
                              >
                                <ScanOutlined />
                                OCR
                              </Button>
                            ) : null}
                            className="u-mt-16"
                            style={{ width: '100%' }}
                          >
                            <Row gutter={16}>
                              <Col span={12} className="u-mt-8">
                                <Typography.Text strong>
                                  {t('employee.idCardFront')}
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong type="danger">
                                  *
                                </Typography.Text>
                                <Controller
                                  name="idCardFrontImage"
                                  render={({
                                    field: { value, onChange }, fieldState: { error }
                                  }) => (
                                    <>
                                      <UploadPhoto
                                        idUpload="idCardFrontImage"
                                        description="Upload hình ảnh"
                                        className="u-mt-8"
                                        // title="Mặt trước"
                                        handleChooseFile={onChange}
                                        handleRemoveFile={() => {
                                          onChange(undefined);
                                        }}
                                        value={value}
                                        big
                                        readOnly={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                      />
                                      {error && (
                                        <span
                                          className="a-input_errorMessage"
                                        >
                                          {error.message}
                                        </span>
                                      )}
                                    </>
                                  )}
                                />
                              </Col>
                              <Col span={12} className="u-mt-8">
                                <Typography.Text strong>
                                  {t('employee.idCardBack')}
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong type="danger">
                                  *
                                </Typography.Text>
                                <Controller
                                  name="idCardBackImage"
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                  }) => (
                                    <>
                                      <UploadPhoto
                                        idUpload="idCardBack"
                                        description="Upload hình ảnh"
                                        className="u-mt-8"
                                        // title="Mặt trước"
                                        handleChooseFile={onChange}
                                        handleRemoveFile={() => {
                                          onChange(undefined);
                                        }}
                                        value={value}
                                        big
                                        readOnly={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                      />
                                      {error && (
                                        <span
                                          className="a-input_errorMessage"
                                        >
                                          {error.message}
                                        </span>
                                      )}
                                    </>
                                  )}
                                />
                              </Col>
                            </Row>
                          </Card>
                        </Row>

                      </Card>
                      <Card title={<Typography.Title level={4}>{t('employee.contract')}</Typography.Title>} className="u-mt-16">
                        <Row gutter={16}>
                          <Col span={24} className="u-mt-8">
                            <Typography.Text strong>
                              {t('employee.contractType')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="contractType"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <DropdownElement
                                  options={contractTypeOptions}
                                  locale="vi"
                                  placeholder={t('employee.contractType')}
                                  isGetOption
                                  value={value}
                                  isShowSearch
                                  error={error?.message}
                                  onChange={onChange}
                                  size="large"
                                  disabled
                                />
                              )}
                            />
                          </Col>
                        </Row>
                      </Card>
                    </Space>
                  </div>
                </Card>
              </Col>
            </Row>
          </FormProvider>
        </div>
      </Spin>
      <Modal title={t('employee.checkExistedTitle')} open={!!existedInfo} onOk={() => onReuseInfo()} onCancel={() => setExistedInfo(null)}>
        <div dangerouslySetInnerHTML={{
          __html: existedInfo?.info ? (
            `${t('employee.identityNumber')} <strong>${existedInfo?.idNumber}</strong> ${renderReuseMessage}.`
          ) : (
            `${t('employee.identityNumber')} <strong>${existedInfo?.idNumber}</strong> ${renderReuseMessage}.`
          )
        }}
        />
      </Modal>
      <Modal
        title={t('employee.idCardNotMatchTitle')}
        open={isNotMatchIdCardNumber}
        onOk={() => {
          setIsNotMatchIdCardNumber(false);
          if (data) {
            const {
              backIdCard, backIdCardPath, frontIdCard, frontIdCardPath
            } = data.staffAssignData;
            method.reset({
              ...method.getValues(),
              idCardFrontPath: frontIdCardPath || '',
              idCardFrontImage: {
                file: undefined,
                pathSubmit: frontIdCard || '',
              },
              idCardBackPath: backIdCardPath || '',
              idCardBackImage: {
                file: undefined,
                pathSubmit: backIdCard || '',
              },
            });
          }
        }}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText={t('system.ok')}
      >
        <div dangerouslySetInnerHTML={{
          __html: t('employee.idCardNotMatch')
        }}
        />
      </Modal>
    </>
  );
};

export default CreateEmployeeAssign;
